import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ProductTemplateDTO, ProductTemplateSummaryDTO, UnitType, UnitCategory, DeliveryOption, ProductTemplateImageInputDTO, UpdateProductTemplateDTO } from '../../../../base/models/models.swagger.generated';
import { ProductTemplateService } from '../product-template.service';
import { CategoryService } from '../../category/category.service';
import { PagedQueryModel } from '../../../../base/shared/table/paged-query-model';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { DropdownModule } from 'primeng/dropdown';
import { SellerProfileDTO } from '../../../../base/models/models.swagger.generated';
import { ProductTemplateEditDialogComponent } from '../product-template-edit-dialog.component';
import { ProductTemplateFormType } from '../product-template-edit-form';

@Component({
  selector: 'app-template-selection',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, ButtonModule, InputTextModule, InputGroupModule, DropdownModule, ProductTemplateEditDialogComponent],
  templateUrl: './template-selection.component.html',
  styleUrls: ['./template-selection.component.scss']
})
export class TemplateSelectionComponent implements OnInit, OnDestroy {
  @Output() templatesSelected = new EventEmitter<{ templateIds: number[], sellerProfileId: number }>();

  public templates: ProductTemplateSummaryDTO[] = [];
  public selectedTemplates: Set<number> = new Set();
  public currentImageIndex: Map<number, number> = new Map();
  public sellerProfiles: SellerProfileDTO[] = [];
  public selectedSellerProfile: number | null = null;
  public editDialogVisible: boolean = false;
  public selectedTemplateForEdit: ProductTemplateDTO | null = null;
  public editForm: FormGroup<ProductTemplateFormType>;
  public categories: any[] = [];
  public deliveryOptions = [
    { label: 'Buyer Pickup', value: DeliveryOption.Pickup },
    { label: 'Seller Delivery', value: DeliveryOption.Delivery }
  ];
  public unitTypeOptions: any[] = [];
  public unitCategoryOptions: any[] = [];
  public templateImages: ProductTemplateImageInputDTO[] = [];
  public existingImages: ProductTemplateImageInputDTO[] = [];
  public removeImageIds: number[] = [];
  public setPrimaryImageId: number | null = null;
  private templateOverrides: Map<number, { price: number; quantity: number }> = new Map();
  private _subscription: Subscription = new Subscription();

  constructor(private productTemplateService: ProductTemplateService, private categoryService: CategoryService, private fb: FormBuilder) { }

  ngOnInit() {
    this.loadTemplates();
    this.loadSellerProfiles();
    this.loadEnumOptions();
    this.loadCategories();
  }

  private loadCategories() {
    const pagedQuery = PagedQueryModel.getDefault();
    this._subscription.add(
      this.categoryService.getData(pagedQuery).subscribe((data) => {
        this.categories = data.items.map(cat => ({ id: cat.categoryId, name: cat.name }));
      })
    );
  }

  private loadEnumOptions() {
    this.unitTypeOptions = Object.keys(UnitType)
      .filter(k => isNaN(Number(k)))
      .map(k => ({ label: k, value: UnitType[k as any] }));
    
    this.unitCategoryOptions = Object.keys(UnitCategory)
      .filter(k => isNaN(Number(k)))
      .map(k => ({ label: k, value: UnitCategory[k as any] }));
  }

  private loadSellerProfiles() {
    this._subscription.add(
      this.productTemplateService.getSellerProfiles().subscribe((profiles) => {
        this.sellerProfiles = profiles;
      })
    );
  }

  private loadTemplates() {
    const pagedQuery = PagedQueryModel.getDefault();
    this._subscription.add(
      this.productTemplateService.getData(pagedQuery).subscribe((data) => {
        this.templates = data.items;
        this.templates.forEach(t => {
          this.templateOverrides.set(t.templateId, { price: t.price, quantity: t.defaultPublishQuantity });
        });
      })
    );
  }

  public getTemplateOverride(templateId: number) {
    if (!this.templateOverrides.has(templateId)) {
      const template = this.templates.find(t => t.templateId === templateId);
      this.templateOverrides.set(templateId, { price: template?.price || 0, quantity: template?.defaultPublishQuantity || 0 });
    }
    return this.templateOverrides.get(templateId)!;
  }

  public increasePrice(templateId: number) {
    const override = this.getTemplateOverride(templateId);
    override.price += 1;
  }

  public decreasePrice(templateId: number) {
    const override = this.getTemplateOverride(templateId);
    if (override.price > 0) override.price -= 1;
  }

  public increaseStock(templateId: number) {
    const override = this.getTemplateOverride(templateId);
    override.quantity += 1;
  }

  public decreaseStock(templateId: number) {
    const override = this.getTemplateOverride(templateId);
    if (override.quantity > 0) override.quantity -= 1;
  }

  public toggleTemplate(templateId: number) {
    if (this.selectedTemplates.has(templateId)) {
      this.selectedTemplates.delete(templateId);
    } else {
      this.selectedTemplates.add(templateId);
    }
  }

  public isSelected(templateId: number): boolean {
    return this.selectedTemplates.has(templateId);
  }

  public proceed() {
    if (this.selectedTemplates.size > 0 && this.selectedSellerProfile) {
      const selectedWithOverrides = Array.from(this.selectedTemplates).map(templateId => ({
        templateId,
        overrides: this.getTemplateOverride(templateId)
      }));
      this.templatesSelected.emit({ templateIds: selectedWithOverrides.map(s => s.templateId), sellerProfileId: this.selectedSellerProfile });
    }
  }

  public getCurrentImage(template: ProductTemplateSummaryDTO): string {
    if (!template.imageUrls || template.imageUrls.length === 0) return template.primaryImageUrl || '';
    const index = this.currentImageIndex.get(template.templateId) || 0;
    return template.imageUrls[index] || template.primaryImageUrl || '';
  }

  public nextImage(templateId: number, event: Event) {
    event.stopPropagation();
    const template = this.templates.find(t => t.templateId === templateId);
    if (template?.imageUrls && template.imageUrls.length > 1) {
      const currentIndex = this.currentImageIndex.get(templateId) || 0;
      this.currentImageIndex.set(templateId, (currentIndex + 1) % template.imageUrls.length);
    }
  }

  public prevImage(templateId: number, event: Event) {
    event.stopPropagation();
    const template = this.templates.find(t => t.templateId === templateId);
    if (template?.imageUrls && template.imageUrls.length > 1) {
      const currentIndex = this.currentImageIndex.get(templateId) || 0;
      this.currentImageIndex.set(templateId, currentIndex === 0 ? template.imageUrls.length - 1 : currentIndex - 1);
    }
  }

  public hasMultipleImages(template: ProductTemplateSummaryDTO): boolean {
    return template.imageUrls && template.imageUrls.length > 1;
  }

  public openEditDialog(templateId: number) {
    this._subscription.add(
      this.productTemplateService.getRecord(templateId).subscribe((template) => {
        this.selectedTemplateForEdit = template;
        this.existingImages = (template.images || []).map(img => new ProductTemplateImageInputDTO(img));
        this.templateImages = [];
        this.removeImageIds = [];
        this.setPrimaryImageId = null;
        this.initEditForm();
        setTimeout(() => this.editDialogVisible = true, 0);
      })
    );
  }

  private initEditForm() {
    const { templateId, templateName, price, unit, unitValue, unitType, unitCategory, defaultPublishQuantity, daysToUnlist, deliveryOption, additionalInformation, description } = this.selectedTemplateForEdit || {};
    const categoryIds = this.selectedTemplateForEdit?.categories?.map(c => c.categoryId).filter(id => id != null) || [];

    this.editForm = this.fb.group<ProductTemplateFormType>({
      Id: this.fb.control<number>(templateId || 0),
      templateName: this.fb.control<string>(templateName || '', Validators.required),
      categoryIds: this.fb.control<number[]>(categoryIds, [Validators.required, Validators.minLength(1)]),
      price: this.fb.control<number>(price ? Number(price) : 0, [Validators.required, Validators.min(0.01)]),
      quantity: this.fb.control<number>(defaultPublishQuantity ? Number(defaultPublishQuantity) : 10, [Validators.required, Validators.min(1)]),
      description: this.fb.control<string>(description || ''),
      deliveryOption: this.fb.control<DeliveryOption>(deliveryOption || DeliveryOption.Pickup),
      daysToUnlist: this.fb.control<number>(daysToUnlist ? Number(daysToUnlist) : 30, Validators.required),
      unit: this.fb.control<string>(unit || ''),
      unitValue: this.fb.control<number>(unitValue ? Number(unitValue) : 1),
      unitType: this.fb.control<UnitType>(unitType),
      unitCategory: this.fb.control<UnitCategory>(unitCategory),
      additionalInformation: this.fb.control<string>(additionalInformation || '')
    });
  }

  public saveEditedTemplate() {
    if (this.editForm.valid) {
      const templateData = this.editForm.value;
      const updateDto = new UpdateProductTemplateDTO({
        templateId: templateData.Id,
        templateName: templateData.templateName,
        description: templateData.description,
        price: templateData.price,
        defaultPublishQuantity: templateData.quantity,
        unit: templateData.unit,
        unitValue: templateData.unitValue,
        unitType: templateData.unitType,
        unitCategory: templateData.unitCategory,
        daysToUnlist: templateData.daysToUnlist,
        deliveryOption: templateData.deliveryOption,
        categoryIds: templateData.categoryIds,
        additionalInformation: templateData.additionalInformation,
        addImages: this.templateImages,
        removeImageIds: this.removeImageIds,
        setPrimaryImageId: this.setPrimaryImageId
      });
      this._subscription.add(
        this.productTemplateService.updateRecord(updateDto).subscribe(() => {
          this.editDialogVisible = false;
          this.loadTemplates();
        })
      );
    }
  }

  public onImageSelected(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      this._subscription.add(
        this.productTemplateService.uploadImage(file, '').subscribe((imageUrl) => {
          this.templateImages.push(new ProductTemplateImageInputDTO({
            imageUrl,
            isPrimary: this.templateImages.length === 0,
            sortOrder: this.templateImages.length
          }));
          event.target.value = '';
        })
      );
    }
  }

  public removeImage(index: number) {
    this.templateImages.splice(index, 1);
    if (this.templateImages.length > 0 && !this.templateImages.some(img => img.isPrimary)) {
      this.templateImages[0].isPrimary = true;
    }
  }

  public removeExistingImage(index: number) {
    const image = this.existingImages[index];
    if (image && (image as any).imageId) {
      this.removeImageIds.push((image as any).imageId);
    }
    this.existingImages.splice(index, 1);
    if (this.existingImages.length > 0 && !this.existingImages.some(img => img.isPrimary)) {
      this.existingImages[0].isPrimary = true;
    }
  }

  public setPrimaryImage(index: number) {
    this.templateImages.forEach((img, i) => {
      img.isPrimary = i === index;
    });
  }

  public setPrimaryExistingImage(index: number) {
    const image = this.existingImages[index];
    this.existingImages.forEach((img, i) => {
      img.isPrimary = i === index;
    });
    if (image && (image as any).imageId) {
      this.setPrimaryImageId = (image as any).imageId;
    }
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}
